if (!customElements.get('aurastep-featured-products')) {
  customElements.define('aurastep-featured-products', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.nav = this.querySelector('[data-carousel-nav]');
      if (!this.track || !this.nav || this.controller) return;
      this.cards = [...this.track.querySelectorAll('[data-carousel-card]')];
      if (!this.cards.length) return;
      this.visible = Math.max(1, Number(this.dataset.visible) || 2);
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.nav.replaceChildren();
      this.dots = Array.from({ length: Math.ceil(this.cards.length / this.visible) }, (_, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = this.dataset.dotClass;
        button.setAttribute('aria-label', `${this.dataset.sliderLabel} ${index + 1}`);
        button.setAttribute('aria-controls', this.track.id);
        button.addEventListener('click', () => { this.stopAutoplay(); this.go(index); }, options);
        this.nav.append(button);
        return button;
      });
      this.nav.hidden = this.dots.length < 2;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        // Preserve browser shortcuts such as Ctrl+Home and Alt+ArrowLeft.
        if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
        let target;
        if (event.key === 'ArrowRight') target = this.current + 1;
        if (event.key === 'ArrowLeft') target = this.current - 1;
        if (event.key === 'Home') target = 0;
        if (event.key === 'End') target = this.dots.length - 1;
        if (target === undefined) return;
        event.preventDefault();
        this.stopAutoplay();
        this.go(target);
      }, options);
      this.addEventListener('pointerdown', () => this.stopAutoplay(), options);
      this.addEventListener('focusin', () => this.stopAutoplay(), options);
      this.addEventListener('mouseenter', () => this.stopAutoplay(), options);
      this.mobile.addEventListener('change', () => { this.update(); this.configureAutoplay(); }, options);
      this.motion.addEventListener('change', () => this.configureAutoplay(), options);
      document.addEventListener('visibilitychange', () => this.configureAutoplay(), options);
      this.observer = new ResizeObserver(() => this.update());
      this.observer.observe(this.track);
      this.update();
      this.configureAutoplay();
    }
    targets() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      return this.dots.map((_, i) => Math.min(max, this.cards[i * this.visible].offsetLeft - this.cards[0].offsetLeft));
    }
    update() {
      if (!this.track.clientWidth) return;
      const targets = this.targets();
      this.current = targets.reduce((best, target, index) => Math.abs(target - this.track.scrollLeft) < Math.abs(targets[best] - this.track.scrollLeft) ? index : best, 0);
      this.dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === this.current);
        dot.setAttribute('aria-current', index === this.current ? 'true' : 'false');
      });
    }
    go(index) {
      const selected = Math.max(0, Math.min(index, this.dots.length - 1));
      this.track.scrollTo({ left: this.targets()[selected], behavior: this.motion.matches ? 'auto' : 'smooth' });
    }
    configureAutoplay() {
      this.stopAutoplay();
      if (this.dataset.autoplay !== 'true' || !this.mobile.matches || this.motion.matches || document.hidden || this.dots.length < 2) return;
      this.interval = setInterval(() => this.go((this.current + 1) % this.dots.length), Math.max(2000, Number(this.dataset.interval) || 5000));
    }
    stopAutoplay() { clearInterval(this.interval); }
    disconnectedCallback() {
      this.stopAutoplay();
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
